<template>
  <div class="strategy-page">
    <section class="strategy-panel">
      <div class="strategy-tabs" role="tablist" aria-label="攻略分类">
        <button
          v-for="category in categories"
          :key="category.id"
          type="button"
          class="strategy-tab"
          :class="{ active: activeCategory === category.id }"
          role="tab"
          :aria-selected="String(activeCategory === category.id)"
          @click="handleCategoryClick(category.id)"
        >
          <span class="tab-title">{{ category.name }}</span>
          <span class="tab-subtitle">{{ category.description }}</span>
        </button>
      </div>

      <div v-if="isPageLoading && !categories.length" class="strategy-grid strategy-grid-loading">
        <article
          v-for="index in 8"
          :key="`strategy-skeleton-${index}`"
          class="strategy-card strategy-card-skeleton"
          aria-hidden="true"
        >
          <div class="card-cover">
            <div class="card-cover-loading"></div>
          </div>
        </article>
      </div>

      <div
        v-for="category in categories"
        :key="category.id"
        :id="'category-' + category.id"
        class="strategy-group"
      >
        <div class="section-head">
          <div>
            <h2 class="section-title">{{ category.name }}</h2>
          </div>
          <button type="button" class="section-more" @click="goToStrategyList(category.id)">查看更多 ></button>
        </div>

        <div class="strategy-grid">
          <template v-if="isPageLoading">
            <article
              v-for="index in 8"
              :key="`strategy-skeleton-${category.id}-${index}`"
              class="strategy-card strategy-card-skeleton"
              aria-hidden="true"
            >
              <div class="card-cover">
                <div class="card-cover-loading"></div>
              </div>
            </article>
          </template>

          <article
            v-else
            v-for="article in articles[category.id]"
            :key="article.id"
            class="strategy-card"
            @click="goToStrategyDetail(category.id, article)"
          >
            <div class="card-cover">
              <div v-if="!isCoverLoaded(article)" class="card-cover-loading"></div>
              <img
                :src="article?.coverImg?.url || fallbackCover"
                :alt="article.title"
                loading="lazy"
                :class="{ loaded: isCoverLoaded(article) }"
                @load="markCoverLoaded(article)"
                @error="handleCoverError($event, article)"
              >
            </div>
            <h3 class="card-title">{{ article.title }}</h3>
          </article>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { useHead } from '@unhead/vue'
import { getStrategyCategoryPageList, getStrategyPageList } from '@/api/strategy'
import { getPageSsgState, setPageSsgState, shouldMarkImageLoaded } from '@/utils/ssg-state'

const SSG_STATE_KEY = 'strategyPage'
export default {
  name: 'StrategyPage',
  setup() {
    useHead({
      title: '装修攻略-自建房攻略-装修避坑指南_干货知识-化化网',
      meta: [
        { name: 'description', content: '化化网攻略频道，覆盖装修攻略、自建房攻略，提供预算规划、合同签署、硬装施工、软装搭配、选材指南、验收入住等实用干货，业主必看的装修与自建房知识库。' },
        { name: 'keywords', content: '装修攻略,自建房攻略,装修避坑,装修知识,自建房避坑,自建房知识,化化网' }
      ]
    })
  },
  data() {
    return {
      activeCategory: '',
      categories: [],
      articles: {},
      coverLoadedMap: {},
      isPageLoading: typeof window !== 'undefined',
      fallbackCover: 'https://obs.huahuaeco.com/official/product2.png'
    }
  },
  created() {
    this.applySsgState()
  },
  serverPrefetch() {
    return this.listStrategyCategoryApi()
  },
  mounted() {
    this.refreshStrategyPage()
  },
  methods: {
    applySsgState() {
      const state = getPageSsgState(this.$route, SSG_STATE_KEY)
      if (!state) {
        return
      }

      this.activeCategory = state.activeCategory || ''
      this.categories = Array.isArray(state.categories) ? state.categories : []
      this.articles = state.articles || {}
      this.coverLoadedMap = state.coverLoadedMap || {}
    },
    persistSsgState() {
      setPageSsgState(this.$route, SSG_STATE_KEY, {
        activeCategory: this.activeCategory,
        categories: this.categories,
        articles: this.articles,
        coverLoadedMap: this.coverLoadedMap
      })
    },
    normalizeListResponse(res) {
      const list = res.data.records || res.data.data || res.data || []
      return Array.isArray(list) ? list : []
    },
    async fetchCategoryChildrenIds(categoryId) {
      const res = await getStrategyCategoryPageList({
        categoryId,
        isExtension: true,
        pageNum: 1,
        pageSize: 100
      })

      return res.data.data
        .map((item) => item && item.id)
        .filter(Boolean)
    },
    async listStrategyCategoryApi() {
      try {
        const res = await getStrategyCategoryPageList({
          categoryId: 0,
          isExtension: true,
          pageNum: 1,
          pageSize: 5
        })

        this.categories = this.normalizeListResponse(res)
        this.activeCategory = this.categories.length > 0 ? this.categories[0].id : ''
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('strategyCategories', JSON.stringify(this.categories))
        }

        const categoriesWithChildren = await Promise.all(
          this.categories.map(async (category) => ({
            ...category,
            childIds: await this.fetchCategoryChildrenIds(category.id)
          }))
        )
        this.categories = categoriesWithChildren
        await Promise.all(
          categoriesWithChildren.map((category) => this.listStrategyApi(category.id, category.childIds))
        )
        this.persistSsgState()
      } catch (error) {
        console.error('Failed to fetch strategy categories:', error)
      }
    },
    async refreshStrategyPage() {
      this.isPageLoading = true
      try {
        await this.listStrategyCategoryApi()
      } finally {
        this.isPageLoading = false
      }
    },
    async listStrategyApi(categoryKey, childIds = []) {
      try {
        const res = await getStrategyPageList({
          categoryIdList: [categoryKey, ...childIds].filter(Boolean),
          pageNum: 1,
          pageSize: 8
        })
        const strategyList = this.normalizeListResponse(res).map((item) => {
          const article = {
            ...item,
            childCategoryId: item.childCategoryId || childIds[0] || ''
          }
          this.coverLoadedMap[article.id] = shouldMarkImageLoaded(article?.coverImg?.url)
          return article
        })
        this.articles[categoryKey] = strategyList
      } catch (error) {
        console.error('Failed to fetch strategies:', error)
      }
    },
    isCoverLoaded(article) {
      if (!article?.id) {
        return true
      }

      return Boolean(this.coverLoadedMap[article.id])
    },
    markCoverLoaded(article) {
      if (!article?.id) {
        return
      }

      this.coverLoadedMap[article.id] = true
    },
    handleCoverError(event, article) {
      const target = event?.target
      if (target && target.getAttribute('src') !== this.fallbackCover) {
        if (article) {
          article.coverImg = {
            ...(article.coverImg || {}),
            url: this.fallbackCover
          }
        }
        target.src = this.fallbackCover
        return
      }

      if (target) {
        target.style.visibility = 'hidden'
      }
      this.markCoverLoaded(article)
    },
    handleCategoryClick(categoryKey) {
      this.activeCategory = categoryKey
      const currentCategory = this.categories.find((category) => category.id === categoryKey)
      if (currentCategory && !this.articles[categoryKey]) {
        this.listStrategyApi(categoryKey, currentCategory.childIds || [])
      }

      this.$nextTick(() => {
        const section = document.getElementById(`category-${categoryKey}`)
        if (!section) return

        section.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },
    async goToStrategyList(categoryId) {
      let res = await getStrategyCategoryPageList({
        categoryId,
        isExtension: true,
        pageNum: 1,
        pageSize: 100
      })
      let childCategoryIds = res.data.data.map(item => item.id)
      this.$router.push({
        path: `/strategy/list/${categoryId}`,
        query: { childCategoryIds: childCategoryIds.join(',') }
      })
    },
    goToStrategyDetail(categoryId, article) {
      const articleId = article.id || article.title
      this.$router.push({
        path: `/strategy/detail/${articleId}`,
        query: {
          categoryId,
          childCategoryId: article.childCategoryId
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
.strategy-page {
  padding: 92px 0 80px;
  min-height: 100vh;
}

.strategy-hero,
.strategy-panel {
  width: min(1200px, calc(100% - 48px));
  margin: 0 auto;
  background: transparent;
}

.strategy-hero {
  margin-bottom: 28px;
  padding: 46px 54px;
  border-radius: 32px;
  background: linear-gradient(135deg, rgba(255, 248, 240, 0.96), rgba(247, 236, 222, 0.92));
  box-shadow: 0 24px 60px rgba(68, 39, 18, 0.08);
}

.hero-copy {
  max-width: 760px;
}

.hero-en {
  margin: 0 0 10px;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0.28em;
  color: #9b7b61;
}

.hero-title {
  margin: 0;
  font-size: 42px;
  line-height: 1.2;
  color: #2b1d14;
}

.hero-desc {
  margin: 16px 0 0;
  max-width: 680px;
  font-size: 16px;
  line-height: 1.8;
  color: #6f5b4d;
}

.strategy-panel {
  padding: 24px 24px 36px;
  border-radius: 32px;
  box-shadow: 0 18px 48px rgba(76, 53, 36, 0.08);
  backdrop-filter: blur(14px);
}

.strategy-tabs {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 14px;
}

.strategy-group {
  scroll-margin-top: 110px;
}

.strategy-group + .strategy-group {
  margin-top: 48px;
}

.strategy-tab {
  border: 0;
  padding: 18px 14px;
  border-radius: 20px;
  background: #fff;
  color: #333;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease, color 0.2s ease;
}

.strategy-tab:hover,
.strategy-tab.active {
  background: linear-gradient(180deg, #ffffff 0%, #f0efef 100%);
  color: #333;
  box-shadow: 0 14px 20px rgba(131, 95, 66, 0.1);
  transform: translateY(-2px);
}

.tab-title,
.tab-subtitle {
  display: block;
  text-align: center;
}

.tab-title {
  font-size: 19px;
  font-weight: 600;
  line-height: 1.3;
}

.tab-subtitle {
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.4;
  color: inherit;
  opacity: 0.72;
}

.section-head {
  margin: 34px 0 24px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.section-title {
  margin: 0;
  font-size: 28px;
  line-height: 1.3;
  color: #333;
}

.section-desc {
  margin: 8px 0 0;
  max-width: 760px;
  font-size: 15px;
  line-height: 1.8;
  color: #333;
}

.section-more {
  border: 0;
  background: transparent;
  padding-top: 8px;
  font-size: 14px;
  line-height: 1.4;
  color: #333;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
}

.strategy-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 24px 18px;
}

.strategy-grid-loading {
  margin-top: 34px;
}

.strategy-card {
  min-width: 0;
  cursor: pointer;
}

.strategy-card-skeleton {
  cursor: default;
}

.card-cover {
  position: relative;
  overflow: hidden;
  border-radius: 18px;
  aspect-ratio: 1 / 0.78;
  background:
    linear-gradient(135deg, rgba(255, 247, 236, 0.92), rgba(231, 207, 181, 0.9)),
    linear-gradient(180deg, #efe4d6 0%, #dfc7af 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.5);
}

.card-cover-loading {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.5) 50%, rgba(255, 255, 255, 0) 100%), #efe7dd;
  background-size: 200% 100%;
  animation: card-cover-shimmer 1.2s ease-in-out infinite;
}

.card-cover img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.24s ease;
}

.card-cover img.loaded {
  opacity: 1;
}

.cover-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  text-align: center;
}

.cover-badge {
  margin: 0 0 12px;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.72);
  font-size: 12px;
  line-height: 1;
  color: #8f6e56;
}

.cover-title {
  margin: 0;
  font-size: 32px;
  line-height: 1.2;
  color: #2c1e15;
}

.cover-subtitle {
  margin: 10px 0 0;
  font-size: 14px;
  line-height: 1.5;
  color: #725a49;
}

.card-title {
  margin: 14px 0 6px;
  font-size: 14px;
  line-height: 1.6;
  color: #2d2119;
  font-weight: 500;
}

.card-summary {
  margin: 0;
  font-size: 14px;
  line-height: 1.75;
  color: #7a6a5d;
}

@keyframes card-cover-shimmer {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

@media (max-width: 1200px) {
  .strategy-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 960px) {
  .strategy-page {
    padding: 84px 0 56px;
  }

  .strategy-hero,
  .strategy-panel {
    width: min(100%, calc(100% - 32px));
  }

  .strategy-tabs {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .strategy-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .hero-title {
    font-size: 34px;
  }
}

@media (max-width: 640px) {
  .strategy-hero {
    padding: 30px 22px;
    border-radius: 24px;
  }

  .strategy-panel {
    padding: 20px 16px 28px;
    border-radius: 24px;
  }

  .strategy-tabs,
  .strategy-grid {
    grid-template-columns: 1fr;
  }

  .section-head {
    flex-direction: column;
  }

  .hero-title {
    font-size: 28px;
  }

  .section-title {
    font-size: 24px;
  }

  .cover-title {
    font-size: 28px;
  }
}
</style>
